import {useEffect} from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  jobsApi,
  type CreateJobRequest,
  type JobListQuery,
  type CreatePlaylistJobRequest,
  type CreateGoogleSearchJobRequest,
  type CreateChannelJobRequest,
  type CreatePinterestProfileJobRequest,
  type CreatePinterestBoardJobRequest,
  type CreateFollowerSweepJobRequest,
  type CreateImageJobRequest,
  type SetJobScheduleRequest
} from './jobs';
import {channelsApi} from './channels';
import {reportingApi, type ReportParameter, type ReportQuery} from './reporting';
import {submissionsDashboardApi} from './submissionsDashboard';
import {pinterestApi} from './pinterest';
import {videosApi} from './videos';
import {type FlagColor} from './flags';
import {
  itemsApi,
  type Item,
  type ItemsPage,
  type ItemsQuery,
  type ItemFilters,
  type RdOptionsScope
} from './items';
import {submissionsApi, type DeliveryGranularity, type SubmissionsQuery} from './submissions';
import {submissionCommentsApi, type SubmissionCommentPayload} from './submissionComments';
import {
  p0AlertsApi,
  type P0AlertsQuery,
  type CreateP0AlertRequest,
  type AmendP0AlertRequest
} from './p0Alerts';
import {p0SlackAlertConfigApi} from './p0SlackAlertConfig';
import {deliveryActionsApi} from './delivery-actions';
import {apolloRelayConfigApi, type UpdateApolloRelayConfigPayload} from './apolloRelayConfig';
import {
  submissionStatusCheckConfigApi,
  type UpdateSubmissionStatusCheckConfigPayload
} from './submissionStatusCheckConfig';
import {switchesApi} from './switches';
import {p0EscalationConfigApi, type UpdateP0EscalationConfigPayload} from './p0EscalationConfig';
import {moderationSettingsApi, type UpdateModerationSettingsPayload} from './moderationSettings';
import {
  escalationApi,
  type CreateEscalationVariantRequest,
  type UpdateEscalationVariantRequest,
  type CreateEscalationContactRuleRequest,
  type UpdateEscalationContactRuleRequest
} from './escalation';
import {
  p0SlackChannelsApi,
  type CreateP0SlackChannelRequest,
  type UpdateP0SlackChannelRequest
} from './p0SlackChannels';
import {feedbackApi, type CreateFeedbackRequest} from './feedback';
import {classificationsApi, type CreateClassificationRequest} from './classifications';
import {configApi, type TopicWriteRequest} from './config';
import {foldersApi, type UpdateFolderRequest} from './folders';
import {jobSharesApi, folderSharesApi, analystsApi, type CreateShareRequest} from './shares';
import {
  hopperConfigsApi,
  type CreateHopperConfigPayload,
  type UpdateHopperConfigPayload
} from './hopper-configs';
import {
  scrapyCollectionsApi,
  type CreateScrapyCollectionRequest,
  type PatchScrapyCollectionRequest,
  type TestRunWindow
} from './scrapyCollections';
import {usersApi} from './users';
import {
  moderationQueueApi,
  type ModerationQueueKind,
  type QueueClaim,
  type TaskLease
} from './moderationQueue';
import {compiledPromptsApi} from './compiledPrompts';
import {
  classificationConfigsApi,
  type CreateVerticalRequest,
  type CreateRiskRequest,
  type UpdateVerticalRequest,
  type UpdateRiskRequest,
  type CreateClassificationConfigRequest,
  type UpdateClassificationConfigRequest
} from './classificationConfigs';
import {
  qaConfigsApi,
  type CreateQaConfigPayload,
  type FireQaConfigRange,
  type UpdateQaConfigPayload
} from './qaConfigs';
import {
  qaReviewApi,
  type QaReviewQuery,
  type QaAccuracyQuery,
  type SubmitQaReviewPayload
} from './qaReview';
import {
  qcReviewApi,
  type QcReviewQuery,
  type QcHistoryQuery,
  type ApproveQcReviewPayload,
  type RejectQcReviewPayload,
  type BulkApproveQcPayload,
  type QcAccuracyQuery
} from './qcReview';
import {
  rejectedSubmissionsApi,
  type RejectedSubmissionCountsQuery,
  type RejectedSubmissionsQuery
} from './rejectedSubmissions';
import {whatsNewApi} from './whatsNew';

/** Centralized query keys so mutations can invalidate precisely. */
export const queryKeys = {
  jobs: ['jobs'] as const,
  job: (id: string) => ['jobs', id] as const,
  jobImage: (id: string) => ['jobs', id, 'image'] as const,
  jobImageMatches: (id: string) => ['jobs', id, 'image-matches'] as const,
  items: (parameters?: ItemsQuery) => ['items', parameters] as const,
  jobItems: (jobId: string, parameters?: {page?: number; q?: string}) =>
    ['jobs', jobId, 'items', parameters] as const,
  allChannels: (parameters?: object) => ['items', 'channels', parameters] as const,
  jobFollowerGraph: (jobId: string) => ['jobs', jobId, 'follower-graph'] as const,
  jobChannels: (jobId: string, filters?: ItemFilters) =>
    ['jobs', jobId, 'channels', filters] as const,
  folderChannels: (folderId: string, filters?: ItemFilters) =>
    ['folders', folderId, 'channels', filters] as const,
  submissionStats: (
    viewAll: boolean,
    submissionType?: 'Actor' | 'Content',
    dateFrom?: string,
    dateTo?: string,
    allVerticals?: boolean
  ) => ['submissions', 'stats', viewAll, submissionType, dateFrom, dateTo, allVerticals] as const,
  submissionPolicyViolationCounts: (parameters?: SubmissionsQuery) =>
    ['submissions', 'policy-violation-counts', parameters] as const,
  submissionDeliveryStats: (
    granularity: DeliveryGranularity,
    buckets: number,
    viewAll: boolean,
    submissionType?: 'Actor' | 'Content',
    allVerticals?: boolean
  ) =>
    [
      'submissions',
      'delivery-stats',
      granularity,
      buckets,
      viewAll,
      submissionType,
      allVerticals
    ] as const,
  classificationConfig: ['config', 'classifications'] as const,
  configList: (type: string) => ['config', type] as const,
  languages: ['config', 'public', 'languages'] as const,
  languageSearch: (q: string) => ['config', 'public', 'languages', 'search', q] as const,
  topics: ['config', 'public', 'topics'] as const,
  allTopics: ['config', 'public', 'topics', 'all'] as const,
  locations: ['config', 'public', 'locations'] as const,
  folders: ['folders'] as const,
  jobShares: (jobId: string) => ['jobs', jobId, 'shares'] as const,
  folderShares: (folderId: string) => ['folders', folderId, 'shares'] as const,
  analysts: (q?: string) => ['analysts', q] as const,
  hopperConfigs: ['hopper-configs'] as const,
  hopperDashboard: ['hopper-configs', 'dashboard'] as const,
  youtubeReasons: ['hopper-configs', 'youtube-reasons'] as const,
  sheetDeliveryAccount: ['hopper-configs', 'sheet-delivery-account'] as const,
  hopperDeliveryActions: (id: string) => ['hopper-configs', id, 'delivery-actions'] as const,
  submissionFieldSample: (vertical: string, submissionType: string) =>
    ['submissions', 'field-sample', vertical, submissionType] as const,
  scrapyCollections: ['scrapy-collections'] as const,
  scrapyCollectionRuns: (id: string, includeTestRuns = false) =>
    ['scrapy-collections', id, 'runs', includeTestRuns] as const,
  scrapyCollectionDailyStats: (days: number, includeTestRuns: boolean) =>
    ['scrapy-collections', 'daily-stats', days, includeTestRuns] as const,
  scrapyCollectionHistory: (id: string) => ['scrapy-collections', id, 'history'] as const,
  scrapyCollectionJob: (id: string, jobId: string) =>
    ['scrapy-collections', id, 'jobs', jobId] as const,
  scrapyCollectionAllItems: (id: string, jobIds: readonly string[]) =>
    ['scrapy-collections', id, 'all-items', jobIds] as const,
  moderationPolicyViolations: ['moderation-queue', 'policy-violations'] as const,
  moderationLanguages: ['moderation-queue', 'languages'] as const,
  users: ['users'] as const,
  me: ['users', 'me'] as const,
  policyViolationPrompts: ['policy-violation-prompts'] as const,
  defaultPrompt: ['default-prompt'] as const,
  classificationConfigs: ['classification-configs'] as const,
  compiledPrompts: ['compiled-prompts'] as const,
  feedbackMinePending: ['feedback', 'mine-pending'] as const,
  whatsNew: ['whats-new'] as const,
  feedbackSubmissions: (showHidden: boolean) => ['feedback-submissions', showHidden] as const,
  p0Alerts: (parameters?: P0AlertsQuery) => ['p0-alerts', parameters] as const,
  p0Alert: (id: string) => ['p0-alerts', id] as const,
  qaConfigs: ['qa-configs'] as const,
  qaPolicyViolations: ['qa-review', 'policy-violations'] as const,
  qaReviewList: (parameters?: QaReviewQuery) => ['qa-review', parameters] as const,
  qaReviewItem: (id: string) => ['qa-review', id] as const,
  qaAccuracy: (parameters?: QaAccuracyQuery) => ['qa-review', 'accuracy', parameters] as const,
  qaVerticalAccuracy: (parameters?: QaAccuracyQuery) =>
    ['qa-review', 'accuracy', 'verticals', parameters] as const,
  qaMyFeedback: ['qa-review', 'my-feedback'] as const,
  rejectedSubmissions: (parameters?: RejectedSubmissionsQuery) =>
    ['rejected-submissions', parameters] as const,
  rejectedSubmissionCounts: (parameters?: RejectedSubmissionCountsQuery) =>
    ['rejected-submissions', 'counts', parameters] as const,
  reviewFeedbackSummaries: ['rejected-submissions', 'summaries'] as const,
  qcMyFeedback: ['rejected-submissions', 'qc-feedback'] as const,
  submissionComments: (submissionId: string) => ['submissions', submissionId, 'comments'] as const,
  submissionCommentsMyFeedback: ['submission-comments', 'my-feedback'] as const,
  submissionHistory: (id: string) => ['submissions', id, 'history'] as const,
  itemThumbnails: (id: string) => ['items', id, 'thumbnails'] as const,
  qcPolicyViolations: ['qc-review', 'policy-violations'] as const,
  qcAccuracy: (parameters?: QcAccuracyQuery) => ['qc-review', 'accuracy', parameters] as const,
  qcVerticalAccuracy: (parameters?: QcAccuracyQuery) =>
    ['qc-review', 'accuracy', 'verticals', parameters] as const,
  qcReviewList: (parameters?: QcReviewQuery) => ['qc-review', parameters] as const,
  qcReviewItem: (id: string) => ['qc-review', id] as const,
  qcHistory: (parameters?: QcHistoryQuery) => ['qc-review', 'history', parameters] as const,
  qcAnalysts: (q?: string) => ['qc-review', 'analysts', q] as const,
  reports: ['reports'] as const,
  report: (id: string, parameters?: ReportQuery) => ['reports', id, parameters] as const,
  submissionsDashboard: (parameters?: ReportQuery) =>
    ['submissions-dashboard', parameters] as const,
  mySwitches: ['my-switches'] as const,
  switches: ['switches'] as const,
  switchList: ['switches', 'list'] as const,
  switchHistory: ['switches', 'history'] as const,
  switchProposals: ['switches', 'proposals'] as const
};

export const useSwitches = () => {
  const query = useQuery({
    queryKey: queryKeys.mySwitches,
    queryFn: async () => usersApi.mySwitches()
  });
  return {...query, isOn: (key: string) => query.data?.switches[key] ?? false};
};

export const useSwitchList = () =>
  useQuery({queryKey: queryKeys.switchList, queryFn: async () => switchesApi.list()});

export const useSwitchHistory = (enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.switchHistory,
    queryFn: async () => switchesApi.history(),
    enabled
  });

export const useSwitchProposals = () =>
  useQuery({queryKey: queryKeys.switchProposals, queryFn: async () => switchesApi.proposals()});

function useSwitchWrite<Variables>(write: (variables: Variables) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSettled: async () =>
      Promise.all([
        qc.invalidateQueries({queryKey: queryKeys.switchList}),
        qc.invalidateQueries({queryKey: queryKeys.switchHistory}),
        qc.invalidateQueries({queryKey: queryKeys.switchProposals})
      ])
  });
}

export const useKillSwitch = () =>
  useSwitchWrite(async ({key, reason}: {key: string; reason: string}) =>
    switchesApi.kill(key, reason)
  );

export const useProposeSwitchChange = () =>
  useSwitchWrite(
    async ({key, payload}: {key: string; payload: Parameters<typeof switchesApi.propose>[1]}) =>
      switchesApi.propose(key, payload)
  );

export const useApproveSwitchProposal = () =>
  useSwitchWrite(async ({id}: {id: string}) => switchesApi.approve(id));

export const useRejectSwitchProposal = () =>
  useSwitchWrite(async ({id}: {id: string}) => switchesApi.reject(id));

export const useJobs = (parameters?: JobListQuery) =>
  useQuery({
    queryKey: [...queryKeys.jobs, parameters],
    queryFn: async () => jobsApi.list(parameters)
  });

/** Filter-chip options for the Investigations sidebar, which no longer loads every job up front. */
export const useJobFacets = () =>
  useQuery({
    queryKey: [...queryKeys.jobs, 'facets'],
    queryFn: async () => jobsApi.facets(),
    staleTime: 60_000
  });

/**
 The image an image_search job was searched with, so an analyst can see it without having to
 remember or re-upload it. Only fetch when a job is actually selected and is an image search.
 */
export const useJobImage = (jobId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.jobImage(jobId ?? ''),
    queryFn: async () => jobsApi.getImage(jobId!),
    enabled: enabled && Boolean(jobId),
    staleTime: Infinity
  });

/**
 The match evidence behind an image_search job's results, so an analyst can see how much Vision found
 against how little of it was a genuine thumbnail match. Same gating as useJobImage.
 */
export const useJobImageMatches = (jobId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.jobImageMatches(jobId ?? ''),
    queryFn: async () => jobsApi.getImageMatches(jobId!),
    enabled: enabled && Boolean(jobId),
    staleTime: Infinity
  });

export const useJob = (id: string) =>
  useQuery({queryKey: queryKeys.job(id), queryFn: async () => jobsApi.get(id)});

export const useItems = (parameters?: ItemsQuery) =>
  useQuery({queryKey: queryKeys.items(parameters), queryFn: async () => itemsApi.list(parameters)});

export const useAllChannels = (
  parameters: Parameters<typeof itemsApi.listAllChannels>[0],
  enabled = true
) =>
  useQuery({
    queryKey: queryKeys.allChannels(parameters),
    queryFn: async () => itemsApi.listAllChannels(parameters),
    enabled
  });

export const useJobItems = (jobId: string, parameters?: {page?: number; q?: string}) =>
  useQuery({
    queryKey: queryKeys.jobItems(jobId, parameters),
    queryFn: async () => itemsApi.listForJob(jobId, parameters)
  });

// Polls while second-hop crawls are still running, then stops.
export const useFollowerGraph = (jobId: string) =>
  useQuery({
    queryKey: queryKeys.jobFollowerGraph(jobId),
    queryFn: async () => itemsApi.followerGraph(jobId),
    // A big network is a big payload, so it is refreshed less often while crawls are still landing.
    refetchInterval(query) {
      const graph = query.state.data;
      if (!graph?.pending_child_jobs) {
        return false;
      }

      return graph.nodes.length > 5000 ? 60_000 : 15_000;
    }
  });

// Every cached useItems / useJobItems page - the queries whose rows an item flag shows on.
export const isItemsPageQuery = ({queryKey}: {queryKey: readonly unknown[]}) =>
  queryKey[0] === 'items' || (queryKey[0] === 'jobs' && queryKey[2] === 'items');

/**
 Sets the caller's own flag on items, patching it into every cached item page first so the picker
 answers immediately, and rolling those pages back if the write fails. The investigations workspace
 keeps its items in local state rather than react-query, so it calls itemsApi.setFlags itself.
 */
export const useSetItemFlags = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({itemIds, color}: {itemIds: string[]; color: FlagColor | null}) =>
      itemsApi.setFlags(itemIds, color),
    async onMutate({itemIds, color}) {
      await qc.cancelQueries({predicate: isItemsPageQuery});
      const previous = qc.getQueriesData<ItemsPage>({predicate: isItemsPageQuery});
      const ids = new Set(itemIds);
      qc.setQueriesData<ItemsPage>({predicate: isItemsPageQuery}, (page) =>
        page
          ? {
              ...page,
              results: page.results.map((item) =>
                ids.has(item.id)
                  ? {...item, flag: color, flag_note: color ? (item.flag_note ?? null) : null}
                  : item
              )
            }
          : page
      );
      return {previous};
    },
    onError(_error, _variables, context) {
      for (const [key, data] of context?.previous ?? []) {
        qc.setQueryData(key, data);
      }
    }
  });
};

/**
 Per-channel counts for "Group by channel", over the whole job rather than whatever's currently
 paged into the investigations view's local `items` state - see ItemsController.ListJobChannels.
 Only enabled once a job is selected and grouping is actually toggled on.

 `refetchIntervalMs`, when passed, keeps this aggregate current for a still-running job - without
 it, this query is a one-time snapshot taken whenever grouping is toggled on: the job-mode channel
 prefetch (see InvestigationsWorkspace's shouldPrefetchJobChannels) re-fires as the job grows and
 keeps existing channels' counts accurate, but a channel that first appears *after* that snapshot
 has no row here at all and stays missing from the grouped view regardless of what the prefetch
 pulls in - refetching this query is the only way to pick up a genuinely new channel. The caller
 passes a value only while the job is still pending/running, matching the same job-status poll's
 3-second cadence, so a completed job's aggregate stops refetching once nothing more will change.
 */
export type ChannelAggregateScope = {kind: 'job' | 'folder'; id: string};

// Job and folder share one endpoint shape (ItemsController.ListChannels/BulkSetFolderItemStatus's
// read-side counterpart) - a folder aggregate is a GROUP BY over every job in it, same as a job's
// is a GROUP BY over its own items, so one hook picking the right URL by scope.kind covers both
// rather than two near-identical query hooks.
export const useChannelAggregates = (
  scope: ChannelAggregateScope | undefined,
  enabled: boolean,
  filters: ItemFilters,
  refetchIntervalMs?: number
) =>
  useQuery({
    queryKey:
      scope?.kind === 'folder'
        ? queryKeys.folderChannels(scope.id, filters)
        : queryKeys.jobChannels(scope?.id ?? '', filters),
    queryFn: async () =>
      scope?.kind === 'folder'
        ? itemsApi.listChannelsForFolder(scope.id, filters)
        : itemsApi.listChannelsForJob(scope!.id, filters),
    enabled: enabled && Boolean(scope),
    ...(refetchIntervalMs ? {refetchInterval: refetchIntervalMs} : {})
  });

// Fetched only once a card's history is expanded (enabled), never per card up front.
export const useRdEvaluationHistory = (jobId: string, itemId: string, enabled: boolean) =>
  useInfiniteQuery({
    queryKey: ['jobs', jobId, 'items', itemId, 'rd-evaluations'] as const,
    queryFn: async ({pageParam}) => itemsApi.rdEvaluations(jobId, itemId, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
    enabled
  });

// Observed-signal option lists for the filter picker; `filters` are the metadata filters without the
// RD selection so the picker can still change it.
export const useRdSignalNames = (
  scope: RdOptionsScope | undefined,
  filters: ItemFilters,
  search: string,
  enabled: boolean
) =>
  useQuery({
    queryKey: ['rd-signal-options', scope, filters, search] as const,
    queryFn: async () =>
      itemsApi.rdSignalNames(scope!, {...filters, ...(search ? {search} : {}), page_size: 100}),
    enabled: enabled && Boolean(scope)
  });

export const useRdSignalValues = (
  scope: RdOptionsScope | undefined,
  name: string,
  filters: ItemFilters,
  search: string
) =>
  useQuery({
    queryKey: ['rd-signal-options', scope, name, filters, search] as const,
    queryFn: async () =>
      itemsApi.rdSignalValues(scope!, name, {
        ...filters,
        ...(search ? {search} : {}),
        page_size: 100
      }),
    enabled: Boolean(scope) && Boolean(name)
  });

export const useSubmissionStats = (
  viewAll = false,
  submissionType?: 'Actor' | 'Content',
  dateFrom?: string,
  dateTo?: string,
  allVerticals = false
) =>
  useQuery({
    queryKey: queryKeys.submissionStats(viewAll, submissionType, dateFrom, dateTo, allVerticals),
    queryFn: async () =>
      submissionsApi.stats(viewAll, submissionType, dateFrom, dateTo, allVerticals)
  });

export const useSubmissionPolicyViolationCounts = (parameters?: SubmissionsQuery) =>
  useQuery({
    queryKey: queryKeys.submissionPolicyViolationCounts(parameters),
    queryFn: async () => submissionsApi.policyViolationCounts(parameters),
    placeholderData: keepPreviousData
  });

export const useSubmissionDeliveryStats = (
  granularity: DeliveryGranularity = 'day',
  buckets = 30,
  viewAll = false,
  submissionType?: 'Actor' | 'Content',
  allVerticals = false
) =>
  useQuery({
    queryKey: queryKeys.submissionDeliveryStats(
      granularity,
      buckets,
      viewAll,
      submissionType,
      allVerticals
    ),
    queryFn: async () =>
      submissionsApi.deliveryStats(granularity, buckets, viewAll, submissionType, allVerticals)
  });

export const useSubmissionHistory = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.submissionHistory(id),
    queryFn: async () => submissionsApi.history(id),
    enabled
  });

export const useItemThumbnails = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.itemThumbnails(id),
    queryFn: async () => itemsApi.thumbnails(id),
    enabled
  });

export function useRecordCalibration(submissionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      comment: string;
      completed: boolean;
      calibration_date: string | null;
    }) => submissionsApi.recordCalibration(submissionId, body),
    async onSuccess() {
      // The calibration only becomes visible through the history timeline, so the panel has to
      // refetch or the note the reviewer just wrote appears to have gone nowhere.
      await qc.invalidateQueries({queryKey: queryKeys.submissionHistory(submissionId)});
      // The QA queue marks rows that have calibration notes, and a note can be recorded from QA review.
      await qc.invalidateQueries({queryKey: ['qa-review']});
    }
  });
}

export const useClassificationConfig = () =>
  useQuery({
    queryKey: queryKeys.classificationConfig,
    queryFn: async () => classificationsApi.listConfig()
  });

export const useConfigList = (type: string) =>
  useQuery({queryKey: queryKeys.configList(type), queryFn: async () => configApi.list(type)});

export const useLanguages = () =>
  useQuery({queryKey: queryKeys.languages, queryFn: async () => configApi.languages()});

/**
 Searches the full language set server-side. `useLanguages` returns only the default-visible
 entries, because the complete curated ISO 639-3 list is ~7,000 rows; this is how the rest are
 reached without ever sending them all to the browser. Idle until the term is worth a request.
 */
export const useLanguageSearch = (q: string) => {
  // Key on the trimmed term, matching what is actually sent - otherwise "en" and "en " are two
  // cache entries for one server response.
  const term = q.trim();

  return useQuery({
    queryKey: queryKeys.languageSearch(term),
    queryFn: async () => configApi.languages(term),
    enabled: term.length >= 2,
    staleTime: 5 * 60 * 1000
  });
};

export const useTopics = () =>
  useQuery({queryKey: queryKeys.topics, queryFn: async () => configApi.topics()});

/**
 Every topic, retired ones included — for filtering submissions, not for tagging new ones. Use
 `useTopics` anywhere an analyst is choosing a value; this list deliberately contains topics they
 are no longer allowed to pick.
 */
export const useAllTopics = () =>
  useQuery({queryKey: queryKeys.allTopics, queryFn: async () => configApi.allTopics()});

/**
 Both topic mutations invalidate two keys: the admin list, which holds every row whatever its
 status, and the analyst list, which the server filters down to what is available today. A status
 or date change moves rows between them, so refreshing only one leaves a stale picker behind.
 */
export function useCreateTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TopicWriteRequest) => configApi.createTopic(body),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.configList('topic')});
      await qc.invalidateQueries({queryKey: queryKeys.topics});
      await qc.invalidateQueries({queryKey: queryKeys.allTopics});
    }
  });
}

export function useUpdateTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, body}: {id: string; body: TopicWriteRequest}) =>
      configApi.updateTopic(id, body),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.configList('topic')});
      await qc.invalidateQueries({queryKey: queryKeys.topics});
      await qc.invalidateQueries({queryKey: queryKeys.allTopics});
    }
  });
}

export const useLocations = () =>
  useQuery({queryKey: queryKeys.locations, queryFn: async () => configApi.locations()});

export const useFolders = () =>
  useQuery({queryKey: queryKeys.folders, queryFn: async () => foldersApi.list()});

export const useHopperConfigs = () =>
  useQuery({queryKey: queryKeys.hopperConfigs, queryFn: async () => hopperConfigsApi.list()});

// Every vertical's target, in the slim shape the dashboard cards read - not scoped to the analyst's
// granted verticals, unlike the full config list above.
export const useDashboardHoppers = () =>
  useQuery({
    queryKey: queryKeys.hopperDashboard,
    queryFn: async () => hopperConfigsApi.dashboard()
  });

// Populates the Pioneer API target's reason/secondary-reason dropdowns - only worth fetching while
// that target is selected, so it's gated by `enabled`.
export const useYoutubeReportReasons = (enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.youtubeReasons,
    queryFn: async () => hopperConfigsApi.youtubeReasons(),
    enabled
  });

// The gateway service account to share a Sheet target with - only fetched while that target is selected.
export const useSheetDeliveryAccount = (enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.sheetDeliveryAccount,
    queryFn: async () => hopperConfigsApi.sheetDeliveryAccount(),
    enabled
  });

// Polls while the delivery actions panel is open (enabled) so a new action shows up without a manual
// refresh; stops polling once the panel closes.
export const useHopperDeliveryActions = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.hopperDeliveryActions(id),
    queryFn: async () => hopperConfigsApi.deliveryActions(id),
    enabled,
    refetchInterval: enabled ? 3000 : false
  });

// Powers the sheet column picker's live data preview - only worth fetching once a vertical and
// submission type are picked, so it's gated by `enabled`.
export const useSubmissionFieldSample = (
  vertical: string,
  submissionType: string,
  enabled: boolean
) =>
  useQuery({
    queryKey: queryKeys.submissionFieldSample(vertical, submissionType),
    queryFn: async () => hopperConfigsApi.fieldSample(vertical, submissionType),
    enabled
  });

export const useScrapyCollections = () =>
  useQuery({
    queryKey: queryKeys.scrapyCollections,
    queryFn: async () => scrapyCollectionsApi.list()
  });

export const useScrapyCollectionRuns = (id: string, enabled = true, includeTestRuns = false) =>
  useQuery({
    queryKey: queryKeys.scrapyCollectionRuns(id, includeTestRuns),
    queryFn: async () => scrapyCollectionsApi.runs(id, {includeTestRuns}),
    enabled: enabled && Boolean(id)
  });

export const useScrapyCollectionHistory = (id: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.scrapyCollectionHistory(id),
    queryFn: async () => scrapyCollectionsApi.history(id),
    enabled: enabled && Boolean(id)
  });

// The API's own cap (ItemsController.MaxPageSize) - requesting it up front cuts the number of
// paginated round trips by ~20x over the default page size, since most jobs fit in a single page.
const ALL_ITEMS_PAGE_SIZE = 1000;

/**
 Every distinct item across the given jobs (each paginated to exhaustion), for the Analysis
 panel's per-term breakdown - which needs every item a collection has ever collected, not one
 run's page. Deduplicated by item id: the same video can be re-collected by more than one run, and
 counting it once per run it appeared in would double-count it in the per-term match rates.
 `jobIds` should already be filtered to completed runs with items; an empty list means nothing to
 fetch rather than an error.
 */
export const useScrapyCollectionAllItems = (
  id: string,
  jobIds: readonly string[],
  enabled = true
) =>
  useQuery({
    queryKey: queryKeys.scrapyCollectionAllItems(id, jobIds),
    async queryFn() {
      // Each job's own pages are inherently sequential (has_more only known after the previous
      // page), but different jobs don't depend on each other - fetching them in parallel rather
      // than one job at a time is most of the win against `/runs`' up-to-100-job list.
      const perJob = await Promise.all(
        jobIds.map(async (jobId) => {
          const jobItems: Item[] = [];
          let page = 1;
          let hasMore = true;
          while (hasMore) {
            const pageData = await itemsApi.listForJob(jobId, {
              page,
              page_size: ALL_ITEMS_PAGE_SIZE
            });
            jobItems.push(...pageData.results);
            hasMore = pageData.has_more;
            page += 1;
          }

          return jobItems;
        })
      );

      const byId = new Map<string, Item>();
      for (const item of perJob.flat()) {
        byId.set(item.id, item);
      }

      return [...byId.values()];
    },
    enabled: enabled && jobIds.length > 0
  });

/**
 Daily collected-item totals across every collection, pre-aggregated server-side. One request for
 the whole chart, carrying every breakdown dimension, so the chart regroups and drills locally.
 */
export const useScrapyCollectionDailyStats = (days: number, includeTestRuns = false) =>
  useQuery({
    queryKey: queryKeys.scrapyCollectionDailyStats(days, includeTestRuns),
    queryFn: async () => scrapyCollectionsApi.dailyStats({days, includeTestRuns})
  });

export const useScrapyCollectionJob = (id: string, jobId: string | null) =>
  useQuery({
    queryKey: queryKeys.scrapyCollectionJob(id, jobId ?? ''),
    queryFn: async () => scrapyCollectionsApi.getJob(id, jobId!),
    enabled: Boolean(id) && Boolean(jobId),
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' || query.state.data?.status === 'running'
        ? 3000
        : false
  });

export const useModerationQueueSettings = () =>
  useQuery({
    queryKey: ['moderation-queue-settings'],
    queryFn: async () => moderationQueueApi.settings()
  });

// Language and kind narrow every count (see moderationQueueApi.policyViolations); each pair is cached on its own.
export const useModerationPolicyViolations = (
  live = false,
  language?: string,
  kind?: ModerationQueueKind
) =>
  useQuery({
    queryKey: [...queryKeys.moderationPolicyViolations, language ?? null, kind ?? null],
    queryFn: async () => moderationQueueApi.policyViolations(language, kind),
    refetchOnWindowFocus: live,
    refetchInterval: live ? 30_000 : false
  });

// The languages with available work, optionally within one policy violation.
export const useModerationLanguages = (
  policyViolation?: string,
  kind?: ModerationQueueKind,
  live = false
) =>
  useQuery({
    queryKey: [...queryKeys.moderationLanguages, policyViolation ?? null, kind ?? null],
    queryFn: async () => moderationQueueApi.languages(policyViolation, kind),
    refetchOnWindowFocus: live,
    refetchInterval: live ? 30_000 : false
  });

export const useUsers = () =>
  useQuery({queryKey: queryKeys.users, queryFn: async () => usersApi.listAll()});

export const useMe = () => useQuery({queryKey: queryKeys.me, queryFn: async () => usersApi.me()});

// ── Mutations (replace the old server actions + router.refresh()) ─────────────

export function useCreateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateJobRequest) => jobsApi.create(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

export function useCreateImageJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateImageJobRequest) => jobsApi.createImage(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

export function useCreateGoogleSearchJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateGoogleSearchJobRequest) => jobsApi.createGoogleSearch(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

export function useCreatePlaylistJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePlaylistJobRequest) => jobsApi.createPlaylist(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

export function useCreateChannelJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateChannelJobRequest) => jobsApi.createChannel(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => jobsApi.remove(id),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

export function useRerunJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => jobsApi.rerun(id),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

export function useSetJobSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, body}: {id: string; body: SetJobScheduleRequest}) =>
      jobsApi.setSchedule(id, body),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

export function useClearJobSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => jobsApi.clearSchedule(id),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

export const useJobShares = (jobId: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.jobShares(jobId),
    queryFn: async () => jobSharesApi.list(jobId),
    enabled: enabled && Boolean(jobId)
  });

export const useFolderShares = (folderId: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.folderShares(folderId),
    queryFn: async () => folderSharesApi.list(folderId),
    enabled: enabled && Boolean(folderId)
  });

export const useAnalystSearch = (q?: string) =>
  useQuery({queryKey: queryKeys.analysts(q), queryFn: async () => analystsApi.search(q)});

export function useCreateJobShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({jobId, body}: {jobId: string; body: CreateShareRequest}) =>
      jobSharesApi.create(jobId, body),
    onSuccess: async (_result, {jobId}) =>
      qc.invalidateQueries({queryKey: queryKeys.jobShares(jobId)})
  });
}

export function useDeleteJobShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({jobId, shareId}: {jobId: string; shareId: string}) =>
      jobSharesApi.remove(jobId, shareId),
    onSuccess: async (_result, {jobId}) =>
      qc.invalidateQueries({queryKey: queryKeys.jobShares(jobId)})
  });
}

export function useCreateFolderShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({folderId, body}: {folderId: string; body: CreateShareRequest}) =>
      folderSharesApi.create(folderId, body),
    onSuccess: async (_result, {folderId}) =>
      qc.invalidateQueries({queryKey: queryKeys.folderShares(folderId)})
  });
}

export function useDeleteFolderShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({folderId, shareId}: {folderId: string; shareId: string}) =>
      folderSharesApi.remove(folderId, shareId),
    onSuccess: async (_result, {folderId}) =>
      qc.invalidateQueries({queryKey: queryKeys.folderShares(folderId)})
  });
}

export function useSetJobFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, folderId}: {id: string; folderId: string | null}) =>
      jobsApi.setFolder(id, folderId),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.jobs});
      await qc.invalidateQueries({queryKey: queryKeys.folders});
    }
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({name, parentFolderId}: {name: string; parentFolderId?: string}) =>
      foldersApi.create(name, parentFolderId),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.folders})
  });
}

/** Rename, move, or both - see UpdateFolderRequest. */
export function useUpdateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, ...body}: {id: string} & UpdateFolderRequest) =>
      foldersApi.update(id, body),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.folders});
      await qc.invalidateQueries({queryKey: queryKeys.jobs});
    }
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => foldersApi.delete(id),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.folders});
      await qc.invalidateQueries({queryKey: queryKeys.jobs});
    }
  });
}

export function useCreateScrapyCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateScrapyCollectionRequest) => scrapyCollectionsApi.create(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.scrapyCollections})
  });
}

export function useUpdateScrapyCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, body}: {id: string; body: PatchScrapyCollectionRequest}) =>
      scrapyCollectionsApi.update(id, body),
    async onSuccess(updated) {
      await qc.invalidateQueries({queryKey: queryKeys.scrapyCollections});
      await qc.invalidateQueries({queryKey: queryKeys.scrapyCollectionHistory(updated.id)});
    }
  });
}

export function useDeleteScrapyCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => scrapyCollectionsApi.delete(id),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.scrapyCollections})
  });
}

export function useRunScrapyCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => scrapyCollectionsApi.run(id),
    async onSuccess(_result, id) {
      // Prefix (no includeTestRuns) so both the chart's real-runs-only cache entry and the Jobs
      // tab's include-test-runs entry invalidate together.
      await qc.invalidateQueries({queryKey: ['scrapy-collections', id, 'runs']});
      await qc.invalidateQueries({queryKey: queryKeys.scrapyCollections});
    }
  });
}

export function useTestRunScrapyCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, window}: {id: string; window?: TestRunWindow}) =>
      scrapyCollectionsApi.testRun(id, window),
    async onSuccess(_result, {id}) {
      await qc.invalidateQueries({queryKey: ['scrapy-collections', id, 'runs']});
    }
  });
}

export function useCreateHopperConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateHopperConfigPayload) => hopperConfigsApi.create(payload),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.hopperConfigs})
  });
}

export function useUpdateHopperConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, payload}: {id: string; payload: UpdateHopperConfigPayload}) =>
      hopperConfigsApi.update(id, payload),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.hopperConfigs})
  });
}

export function useDeleteHopperConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => hopperConfigsApi.delete(id),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.hopperConfigs})
  });
}

export function useFireHopperConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, submissionIds}: {id: string; submissionIds?: string[]}) =>
      hopperConfigsApi.fire(id, submissionIds),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.hopperConfigs});
    }
  });
}

export function useClearHopperDeliveryActions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => hopperConfigsApi.clearDeliveryActions(id),
    onSuccess: async (_result, id) =>
      qc.invalidateQueries({queryKey: queryKeys.hopperDeliveryActions(id)})
  });
}

export function useCreateClassification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateClassificationRequest) => classificationsApi.create(body),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: ['items']});
      // Channel headers carry classified counts and submission status tags too - see
      // queryKeys.jobChannels / folderChannels.
      await qc.invalidateQueries({
        predicate: (query) => query.queryKey[2] === 'channels'
      });
      // A node in the follower graph is coloured by its submission status.
      await qc.invalidateQueries({
        predicate: (query) => query.queryKey[2] === 'follower-graph'
      });
    }
  });
}

// The investigations view (this hook's only caller) keeps its item list in local state, not a
// `useItems`/`useJobItems` query - so invalidating `['items']` here was never observed by anything.
// Only queryKeys.jobs (the sidebar's job list/counts) is real react-query state worth refreshing.
// scope picks job or folder endpoint (BulkSetJobItemStatus / BulkSetFolderItemStatus's single-item
// PATCH has no folder counterpart, so this always targets a job - folder-scoped single-item dismiss
// goes through useBulkSetItemStatus with a one-element id list instead).
export function useSetItemStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      jobId,
      itemId,
      status
    }: {
      jobId: string;
      itemId: string;
      status: 'active' | 'dismissed';
    }) => itemsApi.setJobItemStatus(jobId, itemId, status),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.jobs});
    }
  });
}

export function useBulkSetItemStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      scope,
      itemIds,
      status
    }: {
      scope: ChannelAggregateScope;
      itemIds: string[];
      status: 'active' | 'dismissed';
    }) =>
      scope.kind === 'folder'
        ? itemsApi.bulkSetFolderItemStatus(scope.id, itemIds, status)
        : itemsApi.bulkSetJobItemStatus(scope.id, itemIds, status),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.jobs});
      await qc.invalidateQueries({queryKey: queryKeys.folders});
    }
  });
}

// "Dismiss channel" by author_id rather than an id list - doesn't require the channel's items to
// already be loaded client-side. Invalidates the jobs/folders prefixes so the group header's
// counts (dismissed_count in particular) reflect the change immediately.
export function useBulkSetItemStatusByAuthor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      scope,
      authorId,
      status
    }: {
      scope: ChannelAggregateScope;
      authorId: string;
      status: 'active' | 'dismissed';
    }) =>
      scope.kind === 'folder'
        ? itemsApi.bulkSetFolderItemStatusByAuthor(scope.id, authorId, status)
        : itemsApi.bulkSetJobItemStatusByAuthor(scope.id, authorId, status),
    // The queryKeys.jobs invalidation below is the ['jobs'] prefix, which already covers
    // ['jobs', jobId, 'channels', ...] - no need to invalidate that key separately first.
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.jobs});
      await qc.invalidateQueries({queryKey: queryKeys.folders});
    }
  });
}

export function useServeNextQueueItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (claim: QueueClaim) => moderationQueueApi.next(claim),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.moderationPolicyViolations});
      await qc.invalidateQueries({queryKey: queryKeys.moderationLanguages});
    }
  });
}

// Extends the whole lease (every task of a batch); no cache effect, so it does not refetch the
// overview counts.
export function useHeartbeatQueueItem() {
  return useMutation({
    mutationFn: async (leaseId: string) => moderationQueueApi.heartbeat(leaseId)
  });
}

export function useReleaseQueueItem() {
  return useMutation({
    mutationFn: async ({id, leaseId}: TaskLease) => moderationQueueApi.release(id, leaseId)
  });
}

export function useDismissQueueItem() {
  return useMutation({
    mutationFn: async ({id, leaseId, review}: TaskLease) =>
      moderationQueueApi.dismiss(id, leaseId, review)
  });
}

export function useReclassifyQueueItem() {
  return useMutation({
    mutationFn: async ({
      id,
      leaseId,
      review,
      target,
      reason
    }: TaskLease & {target: string; reason: string}) =>
      moderationQueueApi.reclassify(id, leaseId, {policyViolation: target, reason}, review)
  });
}

export function useUpdateUserVerticals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, verticals}: {id: string; verticals: string[] | null}) =>
      usersApi.updateVerticals(id, verticals),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.users})
  });
}

// ── Compiled Prompts ──

export const useCompiledPrompts = () =>
  useQuery({
    queryKey: queryKeys.compiledPrompts,
    queryFn: async () => compiledPromptsApi.list()
  });

// ── Classification Configs ──

// `enabled` lets a caller that only sometimes needs the tree (the hopper modal, for a Pioneer API
// hopper) skip the request otherwise; every existing caller keeps the default and is unchanged.
export const useClassificationConfigs = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.classificationConfigs,
    queryFn: async () => classificationConfigsApi.list(),
    enabled
  });

// Generic hooks (preferred for multi-level support)
export function useCreateClassificationConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateClassificationConfigRequest) =>
      classificationConfigsApi.create(body),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfigs});
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfig});
    }
  });
}

export function useUpdateClassificationConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, body}: {id: string; body: UpdateClassificationConfigRequest}) =>
      classificationConfigsApi.update(id, body),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfigs});
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfig});
    }
  });
}

export function useSetClassificationHidden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      facet,
      hidden
    }: {
      id: string;
      facet: 'Actor' | 'Content';
      hidden: boolean;
    }) => classificationConfigsApi.setHidden(id, facet, hidden),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfigs});
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfig});
    }
  });
}

// Legacy hooks (deprecated - kept for backward compatibility)
export function useCreateVertical() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateVerticalRequest) =>
      classificationConfigsApi.createVertical(body),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfigs});
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfig});
    }
  });
}

export function useCreateRisk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateRiskRequest) => classificationConfigsApi.createRisk(body),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfigs});
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfig});
    }
  });
}

export function useUpdateVertical() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, body}: {id: string; body: UpdateVerticalRequest}) =>
      classificationConfigsApi.updateVertical(id, body),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfigs});
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfig});
    }
  });
}

export function useUpdateRisk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, body}: {id: string; body: UpdateRiskRequest}) =>
      classificationConfigsApi.updateRisk(id, body),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfigs});
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfig});
    }
  });
}

export function useDeleteClassificationConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => classificationConfigsApi.delete(id),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfigs});
      await qc.invalidateQueries({queryKey: queryKeys.classificationConfig});
    }
  });
}

export function useCreateSingleVideoJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {url_or_video_id: string; intent?: string; platform?: string}) =>
      jobsApi.createSingle(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

export function useCreatePinterestProfileJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePinterestProfileJobRequest) =>
      jobsApi.createPinterestProfile(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

export function useCreateFollowerSweep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateFollowerSweepJobRequest) => jobsApi.createFollowerSweep(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

export function useCreatePinterestBoardJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePinterestBoardJobRequest) => jobsApi.createPinterestBoard(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.jobs})
  });
}

// ── Channel status (read-only YouTube Data API lookups; no cache invalidation) ──

export function useCheckChannelStatuses() {
  return useMutation({
    mutationFn: async (ids: string[]) => channelsApi.status(ids)
  });
}

export function useChannelByHandle() {
  return useMutation({
    mutationFn: async (handle: string) => channelsApi.byHandle(handle)
  });
}

export function useChannelForVideo() {
  return useMutation({
    mutationFn: async (videoId: string) => channelsApi.forVideo(videoId)
  });
}

export function usePinterestPinStatus() {
  return useMutation({
    mutationFn: async (pinId: string) => pinterestApi.pinStatus(pinId)
  });
}

export function usePinterestProfileStatus() {
  return useMutation({
    mutationFn: async ({username, locale}: {username: string; locale: string}) =>
      pinterestApi.profileStatus(username, locale)
  });
}

export function useChannelLatestVideos(channelId: string | undefined) {
  return useQuery({
    queryKey: ['channels', channelId, 'latest-videos'],
    queryFn: async () => channelsApi.latestVideos(channelId ?? ''),
    enabled: Boolean(channelId),
    // YouTube quota is shared with Scrapy and nothing caches server-side, so don't refetch on every open.
    staleTime: 5 * 60 * 1000
  });
}

export function usePinterestLatestPins(username: string | undefined) {
  return useQuery({
    queryKey: ['pinterest', username, 'latest-pins'],
    queryFn: async () => pinterestApi.latestPins(username ?? ''),
    enabled: Boolean(username),
    staleTime: 5 * 60 * 1000
  });
}

export function useVideoStatus() {
  return useMutation({
    mutationFn: async (videoId: string) => videosApi.status(videoId)
  });
}

export function useVideoStatuses() {
  return useMutation({
    mutationFn: async (videoIds: string[]) => videosApi.statuses(videoIds)
  });
}

// ── P0 Alerts ──

export const useP0Alerts = (parameters?: P0AlertsQuery) =>
  useQuery({
    queryKey: queryKeys.p0Alerts(parameters),
    queryFn: async () => p0AlertsApi.list(parameters)
  });

// Infinite-scrolling list for the P0 page. The key stays under 'p0-alerts' so every P0 mutation
// invalidates it with the plain list.
export const useInfiniteP0Alerts = (parameters: Omit<P0AlertsQuery, 'page'>) =>
  useInfiniteQuery({
    queryKey: [...queryKeys.p0Alerts(parameters), 'infinite'] as const,
    initialPageParam: 1,
    queryFn: async ({pageParam}) =>
      p0AlertsApi.list({...parameters, page: pageParam, page_size: 25}),
    getNextPageParam: (last) =>
      last.page * last.page_size < last.total_count ? last.page + 1 : undefined
  });

export const useP0Alert = (id: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.p0Alert(id),
    queryFn: async () => p0AlertsApi.get(id),
    enabled: enabled && Boolean(id)
  });

export function useCreateP0Alert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateP0AlertRequest) => p0AlertsApi.create(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['p0-alerts']})
  });
}

export function useDecideP0Alert() {
  const qc = useQueryClient();
  return useMutation({
    async mutationFn({
      id,
      action,
      reason
    }: {
      id: string;
      action: 'approve' | 'reject' | 'retry-delivery';
      reason?: string;
    }) {
      if (action === 'approve') {
        return p0AlertsApi.approve(id);
      }

      if (action === 'retry-delivery') {
        return p0AlertsApi.retryDelivery(id);
      }

      return p0AlertsApi.reject(id, reason ?? '');
    },
    onSuccess: async () => qc.invalidateQueries({queryKey: ['p0-alerts']})
  });
}

export function useAmendP0Alert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, body}: {id: string; body: AmendP0AlertRequest}) =>
      p0AlertsApi.amend(id, body),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['p0-alerts']})
  });
}

// ── Feedback ──

export function useCreateFeedback() {
  return useMutation({
    mutationFn: async (body: CreateFeedbackRequest) => feedbackApi.create(body)
  });
}

// Admin: listing of everything analysts have submitted.
export const useFeedbackSubmissions = (showHidden: boolean) =>
  useQuery({
    queryKey: queryKeys.feedbackSubmissions(showHidden),
    queryFn: async () => feedbackApi.list(showHidden)
  });

function useSetFeedbackHidden(hidden: boolean) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (hidden ? feedbackApi.hide(id) : feedbackApi.unhide(id)),
    onSuccess: async () =>
      qc.invalidateQueries({queryKey: queryKeys.feedbackSubmissions(false).slice(0, 1)})
  });
}

export const useHideFeedback = () => useSetFeedbackHidden(true);
export const useUnhideFeedback = () => useSetFeedbackHidden(false);

export const useMarkFeedbackJiraCreated = () =>
  useMutation({mutationFn: async (id: string) => feedbackApi.markJiraCreated(id)});

export const useMyPendingFeedback = () =>
  useQuery({
    queryKey: queryKeys.feedbackMinePending,
    queryFn: async () => feedbackApi.minePending(),
    refetchInterval: 60_000
  });

/**
 This deployment's What's new notes. They only change with a deploy, so one fetch serves the
 session: the sidebar's unread dot and the page share it.
 */
export const useWhatsNew = () =>
  useQuery({
    queryKey: queryKeys.whatsNew,
    queryFn: async () => whatsNewApi.list(),
    staleTime: Infinity
  });

export function useAcknowledgeFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => feedbackApi.acknowledge(id),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.feedbackMinePending})
  });
}

// ── P0 Slack Alert Config (global) ──

export const useP0SlackAlertConfig = () =>
  useQuery({queryKey: ['p0-slack-alert-config'], queryFn: async () => p0SlackAlertConfigApi.get()});

export function useUpdateP0SlackAlertConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pendingWebhookUrl,
      deliveryWebhookUrl,
      escalationWebhookUrl,
      testMode,
      testSimulatedStatusCode
    }: {
      pendingWebhookUrl: string | null;
      deliveryWebhookUrl: string | null;
      escalationWebhookUrl: string | null;
      testMode: boolean;
      testSimulatedStatusCode?: number | null;
    }) =>
      p0SlackAlertConfigApi.update({
        pending_webhook_url: pendingWebhookUrl,
        delivery_webhook_url: deliveryWebhookUrl,
        escalation_webhook_url: escalationWebhookUrl,
        test_mode: testMode,
        test_simulated_status_code: testSimulatedStatusCode ?? null
      }),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['p0-slack-alert-config']})
  });
}

export const useP0SlackDeliveryActions = (enabled: boolean) =>
  useQuery({
    queryKey: ['p0-slack-alert-config', 'delivery-actions'],
    queryFn: async () => p0SlackAlertConfigApi.deliveryActions(),
    enabled,
    refetchInterval: enabled ? 3000 : false
  });

export function useClearP0SlackDeliveryActions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => p0SlackAlertConfigApi.clearDeliveryActions(),
    onSuccess: async () =>
      qc.invalidateQueries({queryKey: ['p0-slack-alert-config', 'delivery-actions']})
  });
}

// Retries a hopper/escalation-origin delivery action by re-queuing its submission for the next
// hopper fire (see SubmissionsController.Retry); a P0 Slack action resends directly instead - see
// useResendDeliveryAction.
export function useRetrySubmissionDelivery() {
  return useMutation({
    mutationFn: async (streamId: string) => submissionsApi.retry(streamId)
  });
}

export function useResendDeliveryAction() {
  return useMutation({
    mutationFn: async (id: string) => deliveryActionsApi.resend(id)
  });
}

// ── Submission Status Check Config (global) ──

export const useSubmissionStatusCheckConfig = () =>
  useQuery({
    queryKey: ['submission-status-check-config'],
    queryFn: async () => submissionStatusCheckConfigApi.get()
  });

export function useUpdateSubmissionStatusCheckConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateSubmissionStatusCheckConfigPayload) =>
      submissionStatusCheckConfigApi.update(payload),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['submission-status-check-config']})
  });
}

// ── Apollo relay config (global) ──

export const useApolloRelayConfig = () =>
  useQuery({
    queryKey: ['apollo-relay-config'],
    queryFn: async () => apolloRelayConfigApi.get()
  });

export function useUpdateApolloRelayConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateApolloRelayConfigPayload) =>
      apolloRelayConfigApi.update(payload),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['apollo-relay-config']})
  });
}

// ── P0 Escalation Config (global) ──

export const useP0EscalationConfig = () =>
  useQuery({
    queryKey: ['p0-escalation-config'],
    queryFn: async () => p0EscalationConfigApi.get()
  });

export function useUpdateP0EscalationConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateP0EscalationConfigPayload) =>
      p0EscalationConfigApi.update(payload),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['p0-escalation-config']})
  });
}
// ── Moderation settings (global) ──

export const useModerationSettings = () =>
  useQuery({
    queryKey: ['moderation-settings'],
    queryFn: async () => moderationSettingsApi.get()
  });

export function useUpdateModerationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateModerationSettingsPayload) =>
      moderationSettingsApi.update(payload),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['moderation-settings']})
  });
}

// ── Escalation Variants & Contact Rules ──

export const useEscalationVariants = (policyViolation?: string) =>
  useQuery({
    queryKey: ['escalation-variants', policyViolation],
    queryFn: async () => escalationApi.listVariants(policyViolation)
  });

export function useCreateEscalationVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateEscalationVariantRequest) => escalationApi.createVariant(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['escalation-variants']})
  });
}

export function useUpdateEscalationVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, body}: {id: string; body: UpdateEscalationVariantRequest}) =>
      escalationApi.updateVariant(id, body),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['escalation-variants']})
  });
}

export function useDeleteEscalationVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => escalationApi.deleteVariant(id),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['escalation-variants']})
  });
}

export const useEscalationRules = () =>
  useQuery({
    queryKey: ['escalation-contact-rules'],
    queryFn: async () => escalationApi.listRules()
  });

export function useCreateEscalationRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateEscalationContactRuleRequest) => escalationApi.createRule(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['escalation-contact-rules']})
  });
}

export function useUpdateEscalationRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, body}: {id: string; body: UpdateEscalationContactRuleRequest}) =>
      escalationApi.updateRule(id, body),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['escalation-contact-rules']})
  });
}

export function useDeleteEscalationRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => escalationApi.deleteRule(id),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['escalation-contact-rules']})
  });
}

// ── P0 Slack Channels ──

export const useP0SlackChannels = () =>
  useQuery({
    queryKey: ['p0-slack-channels'],
    queryFn: async () => p0SlackChannelsApi.list()
  });

export function useCreateP0SlackChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateP0SlackChannelRequest) => p0SlackChannelsApi.create(body),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['p0-slack-channels']})
  });
}

export function useUpdateP0SlackChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, body}: {id: string; body: UpdateP0SlackChannelRequest}) =>
      p0SlackChannelsApi.update(id, body),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['p0-slack-channels']})
  });
}

export function useDeleteP0SlackChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => p0SlackChannelsApi.delete(id),
    onSuccess: async () => qc.invalidateQueries({queryKey: ['p0-slack-channels']})
  });
}

export function useResolveEscalation() {
  return useMutation({
    mutationFn: async ({
      policyViolation,
      escalationVariants,
      at
    }: {
      policyViolation?: string;
      escalationVariants?: string[];
      at?: string;
    }) => escalationApi.resolve(policyViolation, escalationVariants, at)
  });
}

export function useExistingSubmissions() {
  return useMutation({
    mutationFn: async ({platform, externalId}: {platform: string; externalId: string}) =>
      submissionsApi.existingByExternalId(platform, externalId)
  });
}

// ── Weekly QA: Configs ──

export const useQaConfigs = () =>
  useQuery({queryKey: queryKeys.qaConfigs, queryFn: async () => qaConfigsApi.list()});

export function useCreateQaConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateQaConfigPayload) => qaConfigsApi.create(payload),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.qaConfigs})
  });
}

export function useUpdateQaConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, payload}: {id: string; payload: UpdateQaConfigPayload}) =>
      qaConfigsApi.update(id, payload),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.qaConfigs})
  });
}

export function useDeleteQaConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => qaConfigsApi.delete(id),
    onSuccess: async () => qc.invalidateQueries({queryKey: queryKeys.qaConfigs})
  });
}

export function useFireQaConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, range}: {id: string; range?: FireQaConfigRange}) =>
      qaConfigsApi.fire(id, range),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.qaConfigs});
    }
  });
}

// ── Weekly QA: Review Queue & Accuracy ──

export const useQaPolicyViolations = () =>
  useQuery({
    queryKey: queryKeys.qaPolicyViolations,
    queryFn: async () => qaReviewApi.policyViolations()
  });

export const useQaReviewItems = (parameters?: QaReviewQuery) =>
  useQuery({
    queryKey: queryKeys.qaReviewList(parameters),
    queryFn: async () => qaReviewApi.list(parameters)
  });

export const useQaReviewItem = (id: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.qaReviewItem(id),
    queryFn: async () => qaReviewApi.get(id),
    enabled: enabled && Boolean(id)
  });

export function useSubmitQaReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, payload}: {id: string; payload: SubmitQaReviewPayload}) =>
      qaReviewApi.submitReview(id, payload),
    async onSuccess(updated) {
      await qc.invalidateQueries({queryKey: queryKeys.qaPolicyViolations});
      await qc.invalidateQueries({queryKey: ['qa-review']});
      qc.setQueryData(queryKeys.qaReviewItem(updated.id), updated);
    }
  });
}

export const useQaAccuracy = (parameters?: QaAccuracyQuery, enabled = true) =>
  useQuery({
    queryKey: queryKeys.qaAccuracy(parameters),
    queryFn: async () => qaReviewApi.accuracy(parameters),
    enabled
  });

export const useQaVerticalAccuracy = (
  parameters?: Pick<QaAccuracyQuery, 'from' | 'to' | 'interval'>,
  enabled = true
) =>
  useQuery({
    queryKey: queryKeys.qaVerticalAccuracy(parameters),
    queryFn: async () => qaReviewApi.verticalAccuracy(parameters),
    enabled
  });

export const useQaMyFeedback = () =>
  useQuery({queryKey: queryKeys.qaMyFeedback, queryFn: async () => qaReviewApi.myFeedback()});

// ── The caller's own QC rejections and QA corrections ──

export const useRejectedSubmissions = (parameters?: RejectedSubmissionsQuery) =>
  useQuery({
    queryKey: queryKeys.rejectedSubmissions(parameters),
    queryFn: async () => rejectedSubmissionsApi.list(parameters),
    placeholderData: keepPreviousData
  });

export const useRejectedSubmissionCounts = (parameters?: RejectedSubmissionCountsQuery) =>
  useQuery({
    queryKey: queryKeys.rejectedSubmissionCounts(parameters),
    queryFn: async () => rejectedSubmissionsApi.counts(parameters),
    placeholderData: keepPreviousData
  });

export const useReviewFeedbackSummaries = () =>
  useQuery({
    queryKey: queryKeys.reviewFeedbackSummaries,
    queryFn: async () => rejectedSubmissionsApi.summaries()
  });

// ── Reviewer comments on a submission ──

export const useSubmissionComments = (submissionId: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.submissionComments(submissionId),
    queryFn: async () => submissionCommentsApi.list(submissionId),
    enabled
  });

export function useCreateSubmissionComment(submissionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SubmissionCommentPayload) =>
      submissionCommentsApi.create(submissionId, payload),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.submissionComments(submissionId)});
    }
  });
}

export function useUpdateSubmissionComment(submissionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, ...payload}: SubmissionCommentPayload & {id: string}) =>
      submissionCommentsApi.update(submissionId, id, payload),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.submissionComments(submissionId)});
    }
  });
}

export function useDeleteSubmissionComment(submissionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => submissionCommentsApi.remove(submissionId, id),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.submissionComments(submissionId)});
    }
  });
}

export const useSubmissionCommentFeedback = () =>
  useQuery({
    queryKey: queryKeys.submissionCommentsMyFeedback,
    queryFn: async () => submissionCommentsApi.myFeedback()
  });

export function useAcknowledgeSubmissionComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => submissionCommentsApi.acknowledge(id),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.submissionCommentsMyFeedback});
    }
  });
}

export const useQcMyFeedback = () =>
  useQuery({
    queryKey: queryKeys.qcMyFeedback,
    queryFn: async () => rejectedSubmissionsApi.qcFeedback()
  });

export function useAcknowledgeQcFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => rejectedSubmissionsApi.acknowledgeQcFeedback(id),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.qcMyFeedback});
    }
  });
}

export function useAcknowledgeQaFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => qaReviewApi.acknowledge(id),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: queryKeys.qaMyFeedback});
    }
  });
}

// ── QC Review: Pre-delivery Gate for Trainee Analysts ──

export const useQcVerticalAccuracy = (parameters?: Pick<QcAccuracyQuery, 'from' | 'to'>) =>
  useQuery({
    queryKey: queryKeys.qcVerticalAccuracy(parameters),
    queryFn: async () => qcReviewApi.verticalAccuracy(parameters)
  });

export const useQcAccuracy = (parameters?: QcAccuracyQuery, enabled = true) =>
  useQuery({
    queryKey: queryKeys.qcAccuracy(parameters),
    queryFn: async () => qcReviewApi.accuracy(parameters),
    enabled
  });

export const useQcPolicyViolations = (allVerticals = false) =>
  useQuery({
    queryKey: [...queryKeys.qcPolicyViolations, allVerticals],
    queryFn: async () => qcReviewApi.policyViolations(allVerticals)
  });

export const useQcReviewItems = (parameters?: QcReviewQuery) =>
  useQuery({
    queryKey: queryKeys.qcReviewList(parameters),
    queryFn: async () => qcReviewApi.list(parameters)
  });

export const useQcHistory = (parameters?: QcHistoryQuery) =>
  useQuery({
    queryKey: queryKeys.qcHistory(parameters),
    queryFn: async () => qcReviewApi.history(parameters)
  });

export const useQcReviewItem = (id: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.qcReviewItem(id),
    queryFn: async () => qcReviewApi.get(id),
    enabled: enabled && Boolean(id)
  });

function useInvalidateQcQueue() {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({queryKey: queryKeys.qcPolicyViolations});
    await qc.invalidateQueries({queryKey: ['qc-review']});
  };
}

export function useApproveQcReview() {
  const invalidate = useInvalidateQcQueue();
  return useMutation({
    mutationFn: async ({id, payload}: {id: string; payload: ApproveQcReviewPayload}) =>
      qcReviewApi.approve(id, payload),
    async onSuccess() {
      await invalidate();
    }
  });
}

export function useRejectQcReview() {
  const invalidate = useInvalidateQcQueue();
  return useMutation({
    mutationFn: async ({id, payload}: {id: string; payload: RejectQcReviewPayload}) =>
      qcReviewApi.reject(id, payload),
    async onSuccess() {
      await invalidate();
    }
  });
}

export function useBulkApproveQcReview() {
  const invalidate = useInvalidateQcQueue();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: BulkApproveQcPayload) => qcReviewApi.bulkApprove(payload),
    async onSuccess() {
      await invalidate();
      await qc.invalidateQueries({queryKey: ['qc-review', 'analysts']});
    }
  });
}

// Bulk approve/reject over the per-item endpoints, so every item still passes its own guards (own-item,
// vertical scope, already-reviewed) and a bad one fails alone instead of sinking the batch.
export function useBulkReviewQcItems() {
  const invalidate = useInvalidateQcQueue();
  const qc = useQueryClient();
  return useMutation({
    async mutationFn({
      ids,
      action,
      notes = ''
    }: {
      ids: string[];
      action: 'approve' | 'reject';
      notes?: string;
    }) {
      const results = await Promise.allSettled(
        ids.map(async (id) =>
          action === 'approve' ? qcReviewApi.approve(id, {}) : qcReviewApi.reject(id, {notes})
        )
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      return {succeeded: results.length - failed, failed};
    },
    async onSuccess() {
      await invalidate();
      await qc.invalidateQueries({queryKey: ['qc-review', 'analysts']});
    }
  });
}

export const useQcAnalysts = (q?: string) =>
  useQuery({queryKey: queryKeys.qcAnalysts(q), queryFn: async () => qcReviewApi.analysts(q)});

// Reporting (apps/reporting-api). The list is what the signed-in user may see; the service filters
// by the token's permissions, so there is nothing to hide client-side.
// `enabled` is off for a user without read:reports, whose list would only come back 403.
export const useReports = (enabled = true) =>
  useQuery({queryKey: queryKeys.reports, queryFn: async () => reportingApi.list(), enabled});

const REPORT_STALE_MS = 5 * 60_000;

export const useReport = (id: string | undefined, parameters?: ReportQuery) =>
  useQuery({
    queryKey: queryKeys.report(id ?? '', parameters),
    queryFn: async () => reportingApi.get(id ?? '', parameters),
    enabled: id !== undefined,
    // The service caches a result for five minutes (REPORTING_CACHE_TTL_SECONDS), so flipping back to
    // a report tab within that reuses what is on screen rather than asking again for the same rows.
    staleTime: REPORT_STALE_MS,
    // Re-running a report with a new date range keeps its previous result on screen until the new one
    // lands; switching to a different report must not, or one report's blocks would show under
    // another's title.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === id ? previous : undefined
  });

// The Submissions dashboard: every block for one set of filters. Re-running with new filters keeps the
// previous result on screen until the new one lands, as a report does.
export const useSubmissionsDashboard = (parameters?: ReportQuery) =>
  useQuery({
    queryKey: queryKeys.submissionsDashboard(parameters),
    queryFn: async () => submissionsDashboardApi.get(parameters),
    staleTime: REPORT_STALE_MS,
    // The dashboard follows the data: it re-reads every few minutes while the tab is in view (a hidden
    // tab waits), so nobody has to ask for a fresh figure.
    refetchInterval: REPORT_STALE_MS,
    placeholderData: (previous) => previous
  });

// Warms the result of every other option of a report's toggles (the Actor | Content switch), so the
// switch lands on rows already fetched. `base` is the query a switch would run from: the applied one
// over the defaults, as the page's draft is. The same cache key as useReport, so the page reads it.
export function usePrefetchReportToggles(
  id: string | undefined,
  parameters: readonly ReportParameter[],
  base: ReportQuery
) {
  const qc = useQueryClient();
  const baseKey = JSON.stringify(base);
  useEffect(() => {
    if (id === undefined) {
      return;
    }

    for (const parameter of parameters) {
      for (const option of parameter.type === 'toggle' ? (parameter.options ?? []) : []) {
        const variant = Object.fromEntries(
          Object.entries({
            ...(JSON.parse(baseKey) as ReportQuery),
            [parameter.name]: option.value
          }).filter(([, value]) => value !== '')
        );
        void qc.prefetchQuery({
          queryKey: queryKeys.report(id, variant),
          queryFn: async () => reportingApi.get(id, variant),
          staleTime: REPORT_STALE_MS
        });
      }
    }
  }, [qc, id, parameters, baseKey]);
}

export function useUpdateQcRequiredVerticals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, verticals}: {id: string; verticals: string[] | null}) =>
      usersApi.updateQcRequiredVerticals(id, verticals),
    async onSuccess() {
      await qc.invalidateQueries({queryKey: ['qc-review', 'analysts']});
      await qc.invalidateQueries({queryKey: queryKeys.users});
      await qc.invalidateQueries({queryKey: queryKeys.me});
    }
  });
}
